<template>
  <v-card color="transparent" elevation="0" class="dashboard-wrapper">
    <v-col v-if="!finishedLoading || isFiltering" class="d-flex justify-center flex-grow mt-16">
      <v-progress-circular indeterminate color="primary" size="64" width="6"></v-progress-circular>
    </v-col>

    <div v-if="finishedLoading && centerData && !isFiltering" class="dashboard-container">
      <v-row class="mb-6 px-4 align-end">
        <div>
          <h1 class="text-h4 white--text font-weight-bold mb-1">Vertrags-Analytics</h1>
          <p class="grey--text text--lighten-1 mb-0">Performance Monitoring & Benchmark</p>
        </div>
        <v-spacer></v-spacer>

        <div class="glass-control pa-1 d-flex mr-4">
          <v-btn
            v-for="range in [12, 24, 36]"
            :key="range"
            x-small
            depressed
            :color="selectedRange === range ? 'success' : 'transparent'"
            class="rounded-lg transition-swing mx-1 btn-text-white"
            @click="selectedRange = range">
            {{ range / 12 }} J
          </v-btn>
        </div>

        <div class="glass-control pa-1 d-flex">
          <v-btn
            x-small
            depressed
            :color="viewMode === 'total' ? 'success' : 'transparent'"
            class="rounded-lg mx-1 btn-text-white"
            @click="viewMode = 'total'">
            Bestand
          </v-btn>
          <v-btn
            x-small
            depressed
            :color="viewMode === 'diff' ? 'success' : 'transparent'"
            class="rounded-lg mx-1 btn-text-white"
            @click="viewMode = 'diff'">
            Fluktuation
          </v-btn>
        </div>
      </v-row>

      <v-row dense class="mb-6">
        <v-col cols="12" md="3" v-for="(stat, i) in mainStats" :key="i">
          <v-card class="stats-card pa-4" dark>
            <div class="text-overline grey--text mb-1">{{ stat.label }}</div>
            <div class="d-flex align-center flex-wrap">
              <span class="text-h5 font-weight-bold mr-2">{{ stat.value }}</span>
              <v-chip v-if="stat.trend !== undefined" x-small :color="stat.trend >= 0 ? 'success' : 'error'" class="font-weight-bold">
                {{ stat.trend >= 0 ? "+" : "" }}{{ Number(stat.trend).toFixed(1) }}%
              </v-chip>
            </div>
            <div class="text-caption grey--text mt-1">{{ stat.subtext }}</div>
          </v-card>
        </v-col>
      </v-row>

      <v-card class="main-chart-card mb-8" dark>
        <v-card-title class="d-flex align-center py-4 px-6">
          <span class="font-weight-light">{{ viewMode === "total" ? "Gesamtentwicklung (Alle Studios)" : "Fluktuation (Alle Studios)" }}</span>
          <v-chip x-small class="ml-4" color="blue-grey darken-3">⌀ {{ centerData.totalStats.average }} Mitglieder</v-chip>
          <v-spacer></v-spacer>
          <v-chip x-small outlined color="grey lighten-1">Analyse: Letzte {{ selectedRange }} Monate</v-chip>
        </v-card-title>
        <v-card-text>
          <apexchart type="bar" height="400" :options="getChartOptions(true)" :series="centerData.mainSeries"></apexchart>
        </v-card-text>
      </v-card>

      <v-row class="mt-8 mb-4 px-2 align-center">
        <v-divider dark class="mr-4"></v-divider>
        <span class="text-overline grey--text text--lighten-1 text-no-wrap">Detailanalyse pro Standort</span>
        <v-divider dark class="ml-4"></v-divider>
      </v-row>

      <v-row justify="center" class="mb-6">
        <v-col cols="12" md="6" lg="4">
          <v-autocomplete
            v-model="selectedStudio"
            :items="centerData.returnData"
            item-text="center"
            item-value="center"
            label="Wähle ein Studio zur Analyse"
            placeholder="Studio suchen..."
            filled
            rounded
            dark
            clearable
            prepend-inner-icon="mdi-magnify"
            class="studio-select"
            hide-details
            return-object></v-autocomplete>
        </v-col>
      </v-row>

      <v-scale-transition mode="out-in">
        <v-row v-if="!selectedStudio" key="placeholder" justify="center" class="mb-12">
          <v-col cols="12" md="8" class="text-center opacity-60">
            <div class="empty-state-container pa-10">
              <v-icon size="80" color="rgba(255,255,255,0.1)">mdi-chart-bar-stacked</v-icon>
              <h3 class="text-h5 font-weight-light white--text mt-4 mb-2">Wähle einen Standort</h3>
              <p class="grey--text">
                Wähle oben ein Studio aus, um detaillierte Vertragsdaten, <br />Zielerreichung und Trends für diesen Standort zu sehen.
              </p>
            </div>
          </v-col>
        </v-row>

        <v-row v-else key="chart" justify="center">
          <v-col cols="12" md="10" lg="8">
            <v-card class="studio-card elevation-10" dark>
              <v-card-title class="d-flex justify-space-between py-4 px-6 secondary-bg">
                <div>
                  <span class="text-h6 font-weight-bold">{{ selectedStudio.center }}</span>
                  <div class="text-caption grey--text mt-n1">Detailansicht</div>
                </div>
                <div class="text-right">
                  <div class="text-h6">⌀ {{ selectedStudio.stats.average }}</div>
                  <div class="text-caption grey--text">Durchschnitt / Monat</div>
                </div>
              </v-card-title>

              <v-divider></v-divider>

              <v-card-text class="px-6 py-6">
                <apexchart type="bar" height="350" :options="getChartOptions(false)" :series="selectedStudio.series"></apexchart>

                <div class="d-flex justify-space-between mt-6 pt-4 border-top">
                  <div class="d-flex flex-column">
                    <span class="text-caption grey--text mb-1">Aktueller IST-Stand</span>
                    <span class="text-h5 white--text">{{ selectedStudio.stats.currentMonth.ist }}</span>
                  </div>

                  <div class="d-flex flex-column text-right">
                    <span class="text-caption grey--text mb-1">Zielerreichung</span>
                    <div>
                      <span class="text-h5 mr-2">{{ selectedStudio.stats.currentMonth.plan }}</span>
                      <v-chip small :color="selectedStudio.stats.currentMonth.diffPercent >= 0 ? 'success' : 'error'" outlined>
                        {{ selectedStudio.stats.currentMonth.diffPercent >= 0 ? "+" : ""
                        }}{{ selectedStudio.stats.currentMonth.diffPercent.toFixed(1) }}%
                      </v-chip>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-scale-transition>
    </div>
  </v-card>
</template>

<script>
import api from "../../../api"
import VueApexCharts from "vue-apexcharts"
import { mapState } from "vuex"

export default {
  name: "HomeDashboardContractDevelopment",
  components: { apexchart: VueApexCharts },
  props: { studios: { type: Array, default: () => [] } },
  data() {
    return {
      finishedLoading: false,
      isFiltering: false,
      rawBackendData: null,
      selectedRange: 12,
      viewMode: "total",
      selectedStudio: null, // NEU: Speichert das ausgewählte Studio-Objekt
    }
  },
  async created() {
    await this.fetchContractDevelopment()
  },
  methods: {
    async fetchContractDevelopment() {
      this.finishedLoading = false
      try {
        const response = await api.fetchContractDevelopment()
        this.rawBackendData = response.centers
      } catch (e) {
        console.error("API Fehler:", e)
      } finally {
        this.finishedLoading = true
      }
    },
    calculatePercentageDiff(actual, planned) {
      if (!planned || planned === 0) return actual > 0 ? 100.0 : 0.0
      return ((actual - planned) / planned) * 100
    },
    getChartOptions(isMainChart) {
      const categories = this.centerData ? this.centerData.categories : []
      const isDiffView = isMainChart && this.viewMode === "diff"

      return {
        chart: {
          stacked: isDiffView,
          toolbar: { show: false },
          background: "transparent",
          foreColor: "#9e9e9e",
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 800,
          },
        },
        plotOptions: { bar: { horizontal: false, columnWidth: "65%", borderRadius: 6 } },
        dataLabels: { enabled: false },
        xaxis: { categories, labels: { style: { fontSize: "10px" } } },
        yaxis: { labels: { style: { fontSize: "10px" } } },
        grid: { borderColor: "rgba(255,255,255,0.05)", strokeDashArray: 4 },
        colors: isDiffView ? ["#00E396", "#008FFB"] : ["#2483af", "#4CAF50"],
        legend: { position: "top", horizontalAlign: "right" },
        tooltip: {
          theme: "dark",
          custom: ({ series, dataPointIndex, w }) => {
            const monat = w.globals.labels[dataPointIndex]
            if (isDiffView) {
              return `<div class="pa-2 custom-tooltip"><b>${monat}</b><br/>Erneuerungen: ${series[0][dataPointIndex]}<br/>Neukunden: ${series[1][dataPointIndex]}</div>`
            }
            const soll = series[0][dataPointIndex],
              ist = series[1][dataPointIndex],
              diff = ist - soll
            let vormonatHtml = ""
            if (dataPointIndex > 0) {
              const prevIst = series[1][dataPointIndex - 1]
              const vDiff = ist - prevIst
              const vPerc = prevIst !== 0 ? (vDiff / prevIst) * 100 : 0
              vormonatHtml = `<div class="mt-2 pt-1 border-top-tooltip d-flex justify-space-between"><span>vs. Vormonat (Ist):</span><b style="color:${
                vDiff >= 0 ? "#10B981" : "#EF4444"
              }">${vDiff >= 0 ? "+" : ""}${vDiff} (${vPerc.toFixed(1)}%)</b></div>`
            }
            return `<div class="pa-3 custom-tooltip-modern" style="min-width: 220px;">
                <div class="mb-2"><strong>${monat}</strong></div>
                <div class="d-flex justify-space-between mb-1"><span>Soll:</span><b>${soll.toLocaleString()}</b></div>
                <div class="d-flex justify-space-between mb-1"><span>Ist:</span><b>${ist.toLocaleString()}</b></div>
                <div class="d-flex justify-space-between pt-1 border-top-tooltip"><span>Differenz:</span><b style="color:${
                  diff >= 0 ? "#10B981" : "#EF4444"
                }">${diff >= 0 ? "+" : ""}${diff.toLocaleString()}</b></div>
                ${vormonatHtml}
              </div>`
          },
        },
      }
    },
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),

    centerData() {
      if (!this.rawBackendData || !this.storeStudios.length) return null
      const allMonths = Object.keys(this.rawBackendData[Object.keys(this.rawBackendData)[0]]).sort()
      const categories = allMonths.slice(-this.selectedRange)
      const returnData = []
      const monthlyTotals = categories.reduce((acc, m) => ({ ...acc, [m]: { ist: 0, plan: 0, new: 0, renewals: 0 } }), {})

      this.studios.forEach((studio) => {
        const stats = this.rawBackendData[studio.code] || {}
        const studioIst = [],
          studioSoll = []
        let lastIst = 0
        categories.forEach((m) => {
          const d = stats[m] || { active: 0, renewals: 0, newSales: 0 }
          const ist = typeof d === "object" ? d.active : d
          let plan = lastIst
          const growth = (this.storeStudios.find((s) => s.code === studio.code)?.growth || []).find((g) => g.date === m)
          if (growth && lastIst > 0) plan += Number(growth.count)
          studioIst.push(ist)
          studioSoll.push(plan)
          monthlyTotals[m].ist += ist
          monthlyTotals[m].plan += plan
          monthlyTotals[m].new += d.newSales || 0
          monthlyTotals[m].renewals += d.renewals || 0
          lastIst = ist
        })
        returnData.push({
          center: studio.name,
          series: [
            { name: "Soll", data: studioSoll },
            { name: "Ist", data: studioIst },
          ],
          stats: {
            average: Math.round(studioIst.reduce((a, b) => a + b, 0) / studioIst.length),
            currentMonth: {
              ist: studioIst[studioIst.length - 1],
              plan: studioSoll[studioSoll.length - 1],
              diffPercent: this.calculatePercentageDiff(studioIst[studioIst.length - 1], studioSoll[studioSoll.length - 1]),
            },
            monthlyGrowth: { value: studioIst[studioIst.length - 1] - (studioIst[studioIst.length - 2] || 0) },
          },
        })
      })

      const lastM = categories[categories.length - 1],
        prevM = categories[categories.length - 2]
      const mainSeries =
        this.viewMode === "total"
          ? [
              { name: "Geplant", data: categories.map((m) => monthlyTotals[m].plan) },
              { name: "IST", data: categories.map((m) => monthlyTotals[m].ist) },
            ]
          : [
              { name: "Erneuerungen", data: categories.map((m) => monthlyTotals[m].renewals) },
              { name: "Neukunden", data: categories.map((m) => monthlyTotals[m].new) },
            ]

      const totalStats = {
        currentMonth: {
          ist: monthlyTotals[lastM].ist,
          plan: monthlyTotals[lastM].plan,
          diffPercent: this.calculatePercentageDiff(monthlyTotals[lastM].ist, monthlyTotals[lastM].plan),
        },
        previousMonth: { month: prevM || "N/A", value: monthlyTotals[prevM]?.ist || 0 },
        average: Math.round(categories.map((m) => monthlyTotals[m].ist).reduce((a, b) => a + b, 0) / categories.length),
        monthlyGrowth: {
          value: monthlyTotals[lastM].ist - (monthlyTotals[prevM]?.ist || 0),
          percent: this.calculatePercentageDiff(monthlyTotals[lastM].ist, monthlyTotals[prevM]?.ist),
        },
        totalGrowth: {
          value: monthlyTotals[lastM].ist - monthlyTotals[categories[0]].ist,
          percent: this.calculatePercentageDiff(monthlyTotals[lastM].ist, monthlyTotals[categories[0]].ist),
        },
      }
      return { categories, returnData, mainSeries, totalStats }
    },

    mainStats() {
      if (!this.centerData) return []
      const s = this.centerData.totalStats
      return [
        { label: "Letzter Monat", value: s.previousMonth.value.toLocaleString() + " Mitg.", subtext: `Stand im ${s.previousMonth.month}` },
        {
          label: "Aktueller Monat",
          value: s.currentMonth.ist.toLocaleString() + " Mitg.",
          trend: s.currentMonth.diffPercent,
          subtext: `Ziel: ${s.currentMonth.plan.toLocaleString()}`,
        },
        {
          label: "Wachstum (Monat)",
          value: (s.monthlyGrowth.value >= 0 ? "+" : "") + s.monthlyGrowth.value.toLocaleString(),
          trend: s.monthlyGrowth.percent,
          subtext: "Vs. Vormonat",
        },
        {
          label: "Wachstum (Gesamt)",
          value: (s.totalGrowth.value >= 0 ? "+" : "") + s.totalGrowth.value.toLocaleString(),
          trend: s.totalGrowth.percent,
          subtext: `Über letzte ${this.selectedRange} Mon.`,
        },
      ]
    },
  },
  watch: {
    selectedRange() {
      this.isFiltering = true
      // Reset selection when range changes to avoid data mismatch
      this.selectedStudio = null
      setTimeout(() => (this.isFiltering = false), 300)
    },
    viewMode() {
      this.isFiltering = true
      setTimeout(() => (this.isFiltering = false), 300)
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-container {
  padding: 10px;
}
.glass-control {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
}
.btn-text-white {
  color: white !important;
  font-weight: 500;
}
.stats-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 18px !important;
}
.main-chart-card {
  background: rgba(30, 30, 45, 0.6) !important;
  backdrop-filter: blur(15px);
  border-radius: 22px !important;
}

/* Neue Styles für Single Studio View */
.studio-card {
  background: rgba(40, 40, 60, 0.8) !important; /* Etwas deckender für besseren Fokus */
  backdrop-filter: blur(20px);
  border-radius: 22px !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;

  .secondary-bg {
    background: rgba(0, 0, 0, 0.2);
  }

  .border-top {
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }
}

.empty-state-container {
  border: 2px dashed rgba(255, 255, 255, 0.1);
  border-radius: 20px;
}
.opacity-60 {
  opacity: 0.6;
}

/* Autocomplete Styling Anpassung */
::v-deep .studio-select {
  .v-input__slot {
    background: rgba(30, 30, 45, 0.8) !important;
    backdrop-filter: blur(10px);
  }
}

.border-top-tooltip {
  border-top: 1px solid rgba(255, 255, 255, 0.15);
}
.custom-tooltip-modern {
  background: rgba(20, 20, 30, 0.95) !important;
  border-radius: 10px;
  color: #eee;
}
</style>
